import type { Set as ImmutableSet } from 'immutable';
import _isNil from 'lodash/isNil';

import type { ChartOfAccountProps } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import { payeesUtils } from 'app-components/flux/payees';
import { clearState } from 'app-components/flux/transaction-rules/transactionRulesForm';
import type {
  TransactionRuleConditionStatusType,
  TransactionRuleType,
} from 'app-components/flux/transaction-rules/transactionRulesTypes';
import type { Tag, TagProps } from 'app-components/flux/tags/tagsTypes';
import type { CpData, TransactionProps } from 'app-components/flux/transactions/transactionsTypes';

export type ChartOfAccountLike = ChartOfAccountProps & {
  toJS?: () => ChartOfAccountProps;
};

export type RuleConditions = {
  if: string[];
  then: string[];
};

type HasToJs = { toJS: () => Tag[] };
type HasToArray = { toArray: () => Tag[] };

const hasToJs = (value: unknown): value is HasToJs => typeof (value as { toJS?: unknown }).toJS === 'function';
const hasToArray = (value: unknown): value is HasToArray =>
  typeof (value as { toArray?: unknown }).toArray === 'function';

// Type guard ensuring we only assert additional cpData shape while remaining compatible with TransactionProps
const hasCpData = (txn?: TransactionProps): txn is TransactionProps & { cpData?: CpData } =>
  typeof txn === 'object' && txn !== null && 'cpData' in txn;

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.length > 0;

export const normalizeChartOfAccount = (coa?: ChartOfAccountLike | null): ChartOfAccountProps | undefined =>
  !coa ? undefined : coa.toJS?.() || coa;

export const getTagIdArray = (tags: TransactionProps['tags']): string[] => {
  if (!tags) return [];

  if (Array.isArray(tags)) {
    return tags.map((tag: TagProps) => tag?.id).filter(isNonEmptyString);
  }

  const tagsValue = tags as unknown;

  if (hasToJs(tagsValue)) {
    return tagsValue
      .toJS()
      .map((tag) => tag?.id)
      .filter(isNonEmptyString);
  }

  if (hasToArray(tagsValue)) {
    return tagsValue
      .toArray()
      .map((tag) => tag?.id)
      .filter(isNonEmptyString);
  }

  return [];
};

export const getNormalizedPayeeNames = (
  transaction?: TransactionProps,
): { statementPayeeName?: string; inferredPayeeName?: string } => {
  const statementPayeeName = payeesUtils.normalizePayeeQCS(
    hasCpData(transaction) ? transaction.cpData?.payee : undefined,
  );
  const inferredPayeeName = payeesUtils.normalizePayeeQCS(
    hasCpData(transaction) ? transaction.cpData?.inferredPayee : undefined,
  );

  return {
    statementPayeeName: statementPayeeName ?? undefined,
    inferredPayeeName: inferredPayeeName ?? undefined,
  };
};

export const getEnabledOverrides = ({
  transaction,
  originalIsReviewed,
}: {
  transaction?: TransactionProps;
  originalIsReviewed?: boolean | null;
}): Partial<TransactionRuleConditionStatusType> => {
  // Detect if isReviewed was modified by comparing current value to original
  // If originalIsReviewed is provided, compare against it
  // Otherwise, fall back to assuming the default for downloaded transactions is false
  const isReviewedModified =
    originalIsReviewed !== undefined
      ? transaction?.isReviewed !== originalIsReviewed
      : transaction?.isReviewed === true;

  return {
    payeeSource: true,
    payeeTarget: transaction?.payee !== transaction?.cpData?.payee,
    amountSource: Boolean(transaction?.amount !== transaction?.cpData?.amount),
    memoTarget: Boolean(transaction?.memo),
    tagsTarget: Boolean(getTagIdArray(transaction?.tags).length),
    reviewedTarget: isReviewedModified,
    excludedTarget: Boolean(transaction?.isExcludedFromF2S || transaction?.isExcludedFromReports),
    coaTarget: Boolean(transaction?.coa?.id !== transaction?.cpData?.inferredCoa?.id),
  };
};

export const getInitialRule = ({
  transaction,
  rule,
  businessCoaIds,
  statementPayeeName,
  txnRuleV2,
}: {
  transaction?: TransactionProps;
  rule?: TransactionRuleType;
  businessCoaIds?: ImmutableSet<string>;
  statementPayeeName?: string;
  txnRuleV2: boolean;
}): TransactionRuleType => {
  if (transaction) {
    const { payee, coa, businessId } = transaction;
    const targetCOA = normalizeChartOfAccount(coa);
    const isBusinessCoa = Boolean(targetCOA && businessCoaIds?.has(targetCOA.id));
    const normalizedPayee = payee ?? undefined;
    const normalizedStatementPayee = statementPayeeName ?? undefined;
    const normalizedBusinessId = businessId ?? undefined;
    // Ensure tagsTarget is always a plain string[] without undefined values (handles Immutable List or Array)
    const tagsTarget = getTagIdArray(transaction.tags);

    return {
      ...clearState,
      payeeSource: {
        payeeType: 'STATEMENT',
        payeeMatchingCriteria: [{ payeeName: normalizedStatementPayee, payeeMatchingOperator: 'CONTAINS' }],
      },
      amountSource: {
        amountMatchingOperator: 'EQUALS',
        amount1: Math.abs(transaction.amount),
        amountType: transaction.amount < 0 ? 'EXPENSE' : 'INCOME',
      },
      accountSource: {
        accountSourceType: 'SPECIFIC',
        accountSourceIds: [transaction.accountId || undefined],
      },
      payeeTarget: normalizedPayee,
      memoTarget: transaction.memo || undefined,
      tagsTarget: tagsTarget,
      reviewedTarget: transaction.isReviewed || undefined,
      excludedTarget: {
        isExcludedFromF2S: !!transaction.isExcludedFromF2S,
        isExcludedFromReports: !!transaction.isExcludedFromReports,
      },
      ...(targetCOA
        ? isBusinessCoa && txnRuleV2
          ? { businessCoaTarget: targetCOA, businessTargetId: normalizedBusinessId }
          : { coaTarget: targetCOA }
        : {}),
      ...(!txnRuleV2 && normalizedBusinessId ? { businessTargetId: normalizedBusinessId } : {}),
    };
  }

  if (rule) {
    return rule;
  }

  return clearState;
};

export const buildRuleConditions = (rule: TransactionRuleType): RuleConditions => {
  const ifConditions: string[] = [];
  const thenConditions: string[] = [];

  if (rule.payeeSource) {
    ifConditions.push('payee');
  }
  if (rule.amountSource) {
    ifConditions.push('amount');
  }
  if (rule.accountSource) {
    ifConditions.push('accounts');
  }
  if (rule.coaSource) {
    ifConditions.push('categories');
  }
  if (rule.payeeTarget) {
    thenConditions.push('payee');
  }
  if (rule.coaTarget || rule.businessCoaTarget) {
    thenConditions.push('category');
  }
  if (rule.tagsTarget) {
    thenConditions.push('tags');
  }
  if (!_isNil(rule.excludedTarget)) {
    thenConditions.push('exclude');
  }
  if (!_isNil(rule.reviewedTarget)) {
    thenConditions.push('reviewed');
  }

  return {
    if: ifConditions,
    then: thenConditions,
  };
};
